import React from 'react';

const tabs = [
  { id: 'home',        icon: '🏠', label: 'Home' },
  { id: 'picks',       icon: '🏈', label: 'Picks' },
  { id: 'leaderboard', icon: '📋', label: 'Weekly' },
  { id: 'season',      icon: '📊', label: 'Season' },
  { id: 'survivor',    icon: '💀', label: 'Survivor' },
  /* Not the clipboard. Weekly used the same 📋, so two of the six destinations
     a normal member sees were identical at a glance, under a label small enough
     that the icon was doing most of the work. */
  { id: 'recap',       icon: '📰', label: 'Recap' },
  { id: 'pools',       icon: '🏆', label: 'Pools' },
];

/* Admin is appended rather than listed, because it is the one destination that
   depends on who is looking. */
const ADMIN_TAB = { id: 'admin', icon: '⚙️', label: 'Admin' };

/**
 * A destination's icon, by id.
 *
 * App.jsx draws its own tab strip on a wide screen and kept a SECOND copy of
 * these glyphs, which is exactly how Weekly and Recap ended up sharing the
 * clipboard in one place after Recap was given a newspaper in the other. Derived
 * from the list above rather than typed out again, so a third copy cannot start.
 */
export const NAV_ICON = Object.fromEntries([...tabs, ADMIN_TAB].map(t => [t.id, t.icon]));

export default function BottomNav({ tab, setTab, isAdmin, showSurvivor = false, onAdminTab, homeBadge = 0 }) {
  // Survivor is opt-in — drop it from the bar unless this user should see it.
  let allTabs = showSurvivor ? tabs : tabs.filter(t => t.id !== 'survivor');
  if (isAdmin) allTabs = [...allTabs, ADMIN_TAB];

  return (
    <>
      {/* Inject bottom-nav styles */}
      <style>{`
        .bottom-nav {
          display: none;
        }
        /*
         * 959, not 640.
         *
         * The top tab strip needs about 950px before brand + tabs + countdown +
         * logout all fit on one line. Between 641 and there it was rendered
         * anyway and simply ran off the side, so every page on an iPad in
         * portrait scrolled sideways. The bottom nav already handles that space
         * well — it just wasn't being given it.
         */
        @media (max-width: 959px) {
          .bottom-nav {
            display: flex;
            position: fixed;
            bottom: 0;
            left: 0;
            right: 0;
            z-index: 200;
            background: rgba(10, 14, 26, 0.97);
            border-top: 2px solid rgba(1,51,105,0.6);
            padding: 6px 0 max(6px, env(safe-area-inset-bottom));
            backdrop-filter: blur(10px);
          }
          .bottom-nav-item {
            flex: 1;
            /* Without this a flex item refuses to shrink below its content, so
               the ellipsis above would never fire and a long label would widen
               its own column at the expense of its neighbours. */
            min-width: 0;
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 2px;
            /* 42px measured, and 44 is the number this whole pass is about.
               Padding alone was not enough: the label shrinks with the tab
               count, so an eight tab bar came back a pixel short. The floor is
               stated outright instead of being a consequence of the text. */
            padding: 5px 2px;
            min-height: 44px;
            justify-content: center;
            border: none;
            background: transparent;
            cursor: pointer;
            -webkit-tap-highlight-color: transparent;
          }
          .bottom-nav-icon {
            font-size: 20px;
            line-height: 1;
            transition: transform 0.15s;
          }
          /* 10px was small enough that the icon carried the meaning and the
             word underneath was decoration. These are the only labels in the app
             a person needs on their first visit, so they are the last place to
             be saving two pixels.

             Sized off the column count rather than fixed, because the bar is not
             always the same bar. Measured in Inter 600, the widest label is
             Survivor at 48.7px at 12px, and eight tabs on a 375px phone leaves
             42.9px a column. A flat 12px would have spilled it. So: 12px for the
             six a normal member sees, and a step down for each extra
             destination, which only Survivor and Admin ever add.

             The ellipsis is the floor under all of that. On anything narrower
             than an iPhone with a full bar the label truncates inside its column
             instead of pushing the bar out of shape. */
          .bottom-nav-label {
            font-size: var(--nav-label, 12px);
            font-weight: 600;
            letter-spacing: 0;
            color: #667;
            white-space: nowrap;
            max-width: 100%;
            overflow: hidden;
            text-overflow: ellipsis;
            transition: color 0.15s;
          }
          .bottom-nav-item.active .bottom-nav-label {
            color: #D50A0A;
          }
          .bottom-nav-item.active .bottom-nav-icon {
            transform: translateY(-2px);
          }
          /* Push page content up so it isn't hidden behind bottom nav */
          .bottom-nav-spacer {
            height: 68px;
          }
          /* Hide top nav tabs on mobile, keep brand + logout */
          .desktop-nav-tabs { display: none !important; }
          .user-badge { display: none !important; }
        }
        @media (min-width: 960px) {
          .bottom-nav-spacer { display: none; }
        }
      `}</style>

      <nav
        className="bottom-nav"
        /* Six destinations is the normal member. Seven means Survivor is on or
           this is an admin; eight means both. */
        style={{ '--nav-label': (allTabs.length <= 6 ? 12 : allTabs.length === 7 ? 11 : 10) + 'px' }}
      >
        {allTabs.map(t => {
          const showBadge = t.id === 'home' && homeBadge > 0;
          return (
            <button
              key={t.id}
              className={`bottom-nav-item ${tab === t.id ? 'active' : ''}`}
              onClick={() => setTab(t.id)}
              style={{ position: 'relative' }}
            >
              <span className="bottom-nav-icon">{t.icon}</span>
              <span className="bottom-nav-label">{t.label}</span>
              {showBadge && (
                <span
                  style={{
                    position: 'absolute',
                    top: 2,
                    right: '28%',
                    minWidth: 16,
                    height: 16,
                    borderRadius: 8,
                    background: '#D50A0A',
                    color: '#fff',
                    fontSize: 10,
                    fontWeight: 800,
                    lineHeight: '16px',
                    padding: '0 5px',
                    boxShadow: '0 0 8px rgba(213,10,10,0.6)'
                  }}
                >{homeBadge > 99 ? '99+' : homeBadge}</span>
              )}
            </button>
          );
        })}
      </nav>
          <div className="bottom-nav-spacer" />
    </>
  );
}
